// Meeting.jsx - Added minimize/maximize for video call modal
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import MeetingControls from "./components/MeetingControls";
import FiltersPanel from "./components/FiltersPanel";
import UserList from "./components/UserList";
import ProfileComparison from "./components/ProfileComparison";
import QuestionPopup from "./components/QuestionPopup";
import { fetchProfiles } from "./api/profiles";
import {
  startMeeting,
  endMeeting,
  getAllMeetingResultsByUsers,
  getDateQuestions,
  logMeetingQuestion,
  getMeetingTrajectory,
  getActiveMeeting,
} from "./api/meetings";
import "./Meeting.css";
import { toast } from "../../components/Common/Toast";
import ToolTips from "./components/ToolTips";
import { convertSnakeCaseInText, toNormalText } from "../../utils/helpers";
import VideoCall from "./components/VideoCall";
import MeetingHeader from "./components/MeetingHeader";
import CompatibilityResults from "./components/CompatibilityResults";
import config from "../../config";
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaWindowMinimize, FaWindowMaximize } from "react-icons/fa";
import { MdCallEnd } from "react-icons/md";

const defaultCaptureModes = {
  wearables: true,
  voice: true,
  camera: false,
};

const getContextConfig = (mode) => {
  switch (mode) {
    case "video_call":
      return {
        meetingContext: "video_call",
        captureModes: { wearables: true, voice: true, camera: true },
      };
    case "audio_call":
      return {
        meetingContext: "audio_call",
        captureModes: { wearables: true, voice: true, camera: false },
      };
    case "verbal_audio":
      return {
        meetingContext: "verbal_audio",
        captureModes: { wearables: true, voice: true, camera: false },
      };
    case "in_person":
    default:
      return {
        meetingContext: "in_person",
        captureModes: { wearables: true, voice: true, camera: false },
      };
  }
};

const Meeting = () => {
  const user = useSelector((state) => state.user.userInfo);
  const token = useSelector((state) => state.user.token);

  // Existing states
  const [selectedUser, setSelectedUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [genderFilter, setGenderFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [meetingStartTime, setMeetingStartTime] = useState(null);
  const [liveResult, setLiveResult] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [allMeetingsData, setAllMeetingsData] = useState(null);
  const [selectedWindow, setSelectedWindow] = useState("latest");
  const [trajectoryLoading, setTrajectoryLoading] = useState(false);
  const [relationshipTrajectory, setRelationshipTrajectory] = useState(null);
  const [showQuestionPopup, setShowQuestionPopup] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [askedQuestionIds, setAskedQuestionIds] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [showToolTipPopup, setShowToolTipPopup] = useState(false);
  const [stateDetails, setStateDetails] = useState([]);
  const [showStartMeetingPopup, setShowStartMeetingPopup] = useState(false);
  const [isVideoMeeting, setIsVideoMeeting] = useState(null);
  const [selectedMeetingMode, setSelectedMeetingMode] = useState("in_person");
  const [startingMeeting, setStartingMeeting] = useState(false);
  const [addDebugLog, setAddDebugLog] = useState('');

  // New states for video call modal
  const [showVideoCallModal, setShowVideoCallModal] = useState(false);
  const [isVideoCallMinimized, setIsVideoCallMinimized] = useState(false);

  const videoCallRef = useRef(null);
  const firstTimeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const askedQuestionsRef = useRef([]);
  const mountedRef = useRef(true);

  // Helper functions (unchanged)
  const normalizeQuestion = (q) => {
    if (!q) return null;
    return {
      ...q,
      prompt: q.prompt || q.text || q.question || "",
      resolvedCategory: q.signal_type || q.category || q.question_category || "unknown",
    };
  };

  const trendMeaning = (v) => {
    if (!v) return "";
    if (v === "improving") return "Improving over time.";
    if (v === "stable") return "Stable / consistent.";
    if (v === "draining") return "Requires more effort / trending down.";
    if (v === "insufficient_data") return "Not enough data yet.";
    return String(v);
  };

  const getWindowLabel = (windowKey) => {
    if (windowKey === "7d") return "7 days";
    if (windowKey === "30d") return "30 days";
    if (windowKey === "90d") return "90 days";
    return "selected period";
  };

  const stateMap = useMemo(() => {
    return (stateDetails || []).reduce((acc, item) => {
      if (item?.state) acc[item.state] = item.meaning;
      return acc;
    }, {});
  }, [stateDetails]);

  const sharedStateMeaning = (state) => {
    if (!state) return "";
    return stateMap[state] || "";
  };

  // API calls (unchanged)
  const checkActiveMeeting = async () => {
    if (!user?.id) return;

    try {
      const data = await getActiveMeeting(user.id);
      if (!mountedRef.current) return;

      if (data?.success && data?.active_meeting) {
        const activeMeet = data.active_meeting;

        setActiveMeeting({
          meeting_id: activeMeet.meeting_id,
          user_1: activeMeet.user_1,
          user_2: activeMeet.user_2,
          start_time: activeMeet.start_time,
          meeting_type: activeMeet.meeting_type,
          meeting_context: activeMeet.meeting_context,
          capture_modes: activeMeet.capture_modes || defaultCaptureModes,
          zoom_meeting_link: activeMeet.zoom_meeting_link || null,
        });

        setMeetingStartTime(activeMeet.start_time);

        const currentUserId = String(user.id);
        const isUser1 = String(activeMeet.user_1) === currentUserId;
        const isUser2 = String(activeMeet.user_2) === currentUserId;

        let otherUser = null;

        if (isUser1 && activeMeet.user_2_details) {
          otherUser = {
            user_id: activeMeet.user_2,
            first_name: activeMeet.user_2_details.first_name,
            last_name: activeMeet.user_2_details.last_name,
            profile_image: activeMeet.user_2_details.profile_image,
            profession: activeMeet.user_2_details.profession,
          };
        } else if (isUser2 && activeMeet.user_1_details) {
          otherUser = {
            user_id: activeMeet.user_1,
            first_name: activeMeet.user_1_details.first_name,
            last_name: activeMeet.user_1_details.last_name,
            profile_image: activeMeet.user_1_details.profile_image,
            profession: activeMeet.user_1_details.profession,
          };
        } else if (activeMeet.user_2_details) {
          otherUser = {
            user_id: activeMeet.user_2,
            first_name: activeMeet.user_2_details.first_name,
            last_name: activeMeet.user_2_details.last_name,
            profile_image: activeMeet.user_2_details.profile_image,
            profession: activeMeet.user_2_details.profession,
          };
        }

        if (otherUser) {
          setSelectedUser(otherUser);
          setProfiles((prev) => {
            const exists = prev.some(
              (p) => String(p.user_id) === String(otherUser.user_id)
            );
            return exists ? prev : [...prev, otherUser];
          });
        }
      }
    } catch (err) {
      console.error("Error checking active meeting:", err);
    }
  };

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const data = await fetchProfiles(user.id);
      if (data?.success) {
        setProfiles(data.users || []);
      } else {
        toast.error("Failed to load profiles");
      }
    } catch (err) {
      toast.error("Error loading profiles. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      setIsLoadingQuestions(true);
      const data = await getDateQuestions();
      if (data?.success && Array.isArray(data?.questions)) {
        setAvailableQuestions(data.questions.map(normalizeQuestion).filter(Boolean));
      }
    } catch (err) {
      console.error("Error loading questions:", err);
      toast.error("Failed to load questions");
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  const fetchAllResults = async () => {
    const user1 = user?.id;
    const user2 = selectedUser?.user_id;
    if (!user1 || !user2) return;

    try {
      setResultsLoading(true);
      const data = await getAllMeetingResultsByUsers(user1, user2);
      if (!mountedRef.current) return;

      if (data?.success) {
        setAllMeetingsData(data);
        setStateDetails(Array.isArray(data?.state_detail) ? data.state_detail : []);
        const firstComputed = Array.isArray(data.meetings) ? data.meetings.find((m) => !!m.result) : null;
        if (firstComputed?.result) {
          setLiveResult({
            result: firstComputed.result,
            updatedAt: firstComputed.updatedAt || null,
            meeting_id: firstComputed.meeting_id,
            source: firstComputed.source || "unknown",
          });
        } else {
          setLiveResult(null);
        }
      } else {
        setAllMeetingsData(null);
        setLiveResult(null);
      }
    } catch (err) {
      console.error("Error fetching results by users:", err);
      if (!mountedRef.current) return;
      setAllMeetingsData(null);
      setLiveResult(null);
    } finally {
      if (mountedRef.current) setResultsLoading(false);
    }
  };

  const fetchRelationshipTrajectory = async (windowKey) => {
    const user1 = user?.id;
    const user2 = selectedUser?.user_id;
    if (!user1 || !user2) return;

    try {
      setTrajectoryLoading(true);
      const data = await getMeetingTrajectory(user1, user2, windowKey);
      if (!mountedRef.current) return;
      setRelationshipTrajectory(data?.success ? data : null);
      if (Array.isArray(data?.state_detail)) {
        setStateDetails(data.state_detail);
      }
    } catch (err) {
      console.error("Error fetching relationship trajectory:", err);
      if (!mountedRef.current) return;
      setRelationshipTrajectory(null);
      toast.error("Failed to load relationship trajectory.");
    } finally {
      if (mountedRef.current) setTrajectoryLoading(false);
    }
  };

  // Event handlers
  const handleUserSelect = (u) => {
    if (activeMeeting) {
      toast.info("Cannot change selected user while meeting is active");
      return;
    }
    setSelectedUser(u);
  };

  const handleDeselectUser = () => {
    setSelectedUser(null);
    setAllMeetingsData(null);
    setLiveResult(null);
    setRelationshipTrajectory(null);
    setSelectedWindow("latest");
  };

  const handleStartMeeting = () => {
    if (!selectedUser?.user_id) {
      toast.error("Please select a user to start a meeting");
      return;
    }
    if (activeMeeting) {
      toast.error("You already have an active meeting. Please end it first.");
      return;
    }
    setIsVideoMeeting(null);
    setSelectedMeetingMode("in_person");
    setShowStartMeetingPopup(true);
  };

  const closeStartMeetingPopup = () => {
    if (startingMeeting) return;
    setShowStartMeetingPopup(false);
    setIsVideoMeeting(null);
    setSelectedMeetingMode("in_person");
  };

  const handleConfirmStartMeeting = async () => {
    if (!selectedUser?.user_id) {
      toast.error("Please select a user to start a meeting");
      return;
    }

    const { meetingContext, captureModes } = getContextConfig(selectedMeetingMode);

    try {
      setStartingMeeting(true);
      setMeetingStartTime(new Date().toISOString());

      const data = await startMeeting(user.id, selectedUser.user_id, "marriage", meetingContext, captureModes);

      if (data?.success) {
        setActiveMeeting({
          ...data.meeting,
          capture_modes: data.meeting?.capture_modes || captureModes,
          meeting_context: data.meeting?.meeting_context || meetingContext,
        });
        toast.success("Meeting started");
        setShowStartMeetingPopup(false);

        // Show video call modal if meeting context is video or audio
        if (meetingContext === "video_call" || meetingContext === "audio_call") {
          setShowVideoCallModal(true);
          setIsVideoCallMinimized(false);
        }
      } else {
        toast.error("Failed to start meeting");
      }
    } catch (err) {
      toast.error("Error starting meeting");
      console.error("Error starting meeting:", err);
    } finally {
      setStartingMeeting(false);
    }
  };

  const handleEndMeeting = async () => {
    if (!activeMeeting?.meeting_id) return;

    try {
      const u1 = String(activeMeeting.user_1);
      const u2 = String(activeMeeting.user_2);
      const meetingId = activeMeeting.meeting_id;

      setResultsLoading(true);
      
      // Get analytics data from VideoCall component
      let cameraMetrics = null;
      let voiceMetrics = null;
      if (videoCallRef.current && typeof videoCallRef.current.getAnalyticsData === 'function') {
        const analyticsData = videoCallRef.current.getAnalyticsData();
        cameraMetrics = analyticsData.cameraMetrics;
        voiceMetrics = analyticsData.voiceMetrics;
      } else {
        console.log("VideoCall ref not available or getAnalyticsData not a function");
      }

      if (cameraMetrics && voiceMetrics) {
        // console.log("Sending analytics data from parent...");
        
        // Prepare camera payload
        const cameraPayload = {
          user1: u1,
          user2: u2,
          senderUserId: String(user.id),
          metrics: {}
        };

        Object.keys(cameraMetrics).forEach(key => {
          if (cameraMetrics[key] && cameraMetrics[key].length > 0) {
            cameraPayload.metrics[key] = cameraMetrics[key];
          } else {
            cameraPayload.metrics[key] = [{
              ts: Math.floor(Date.now() / 1000),
              value: 0.5
            }];
          }
        });

        // Prepare voice payload
        const voicePayload = {
          user1: u1,
          user2: u2,
          senderUserId: String(user.id),
          metrics: {}
        };

        Object.keys(voiceMetrics).forEach(key => {
          if (voiceMetrics[key] && voiceMetrics[key].length > 0) {
            voicePayload.metrics[key] = voiceMetrics[key];
          } else {
            voicePayload.metrics[key] = [{
              ts: Math.floor(Date.now() / 1000),
              value: key === 'interrupt_patterns' ? 0 : 0.5
            }];
          }
        });

        try {
          // Send camera analytics
          const cameraResponse = await fetch(`${config.baseURL}/api/v1/meetings/${meetingId}/camera-live`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(cameraPayload)
          });

          if (cameraResponse.ok) {
            // console.log("Camera analytics sent successfully from parent");
          } else {
            const errorText = await cameraResponse.text();
            console.error(`Failed to send camera analytics: ${cameraResponse.status} - ${errorText}`);
          }

          // Send voice analytics
          const voiceResponse = await fetch(`${config.baseURL}/api/v1/meetings/${meetingId}/voice-live`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(voicePayload)
          });

          if (voiceResponse.ok) {
            // console.log("Voice analytics sent successfully from parent");
          } else {
            const errorText = await voiceResponse.text();
            console.error(`Failed to send voice analytics: ${voiceResponse.status} - ${errorText}`);
          }
        } catch (err) {
          console.error("Error sending analytics from parent:", err);
        }
      } else {
        console.log("No analytics data available from VideoCall");
      }

      const data = await endMeeting(activeMeeting.meeting_id, u1, u2);
      if (data?.success) {
        toast.success("Meeting ended successfully");
        setShowVideoCallModal(false);
        setIsVideoCallMinimized(false);
        if (firstTimeoutRef.current) clearTimeout(firstTimeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
        setShowQuestionPopup(false);
        setCurrentQuestion(null);
        setActiveMeeting(null);
        setMeetingStartTime(null);

        // Refresh results after a delay
        setTimeout(async () => {
          if (!mountedRef.current) return;
          await fetchAllResults();
          if (selectedWindow !== "latest") {
            await fetchRelationshipTrajectory(selectedWindow);
          }
        }, 3000);
      } else {
        toast.error("Failed to end meeting");
      }
    } catch (err) {
      toast.error("Error ending meeting");
      console.error("Error ending meeting:", err);
    } finally {
      if (mountedRef.current) setResultsLoading(false);
    }
  };

  const toggleVideoCallMinimize = () => {
    setIsVideoCallMinimized(prev => !prev);
  };

  // Effects (unchanged)
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => { if (user?.id) { checkActiveMeeting(); loadProfiles(); loadQuestions(); } }, []);
  useEffect(() => { if (user?.id && selectedUser?.user_id) fetchAllResults(); }, [user?.id, selectedUser?.user_id]);
  useEffect(() => { if (selectedWindow !== "latest") fetchRelationshipTrajectory(selectedWindow); }, [selectedWindow]);
  useEffect(() => {
    if (firstTimeoutRef.current) clearTimeout(firstTimeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    setShowQuestionPopup(false);
    setCurrentQuestion(null);
    setAskedQuestionIds([]);
    askedQuestionsRef.current = [];

    const meetingId = activeMeeting?.meeting_id;
    const questionsReady = Array.isArray(availableQuestions) && availableQuestions.length > 0;

    if (!meetingId || !questionsReady) return;

    const QUESTION_FIRST_DELAY = 2 * 60 * 1000;
    const QUESTION_INTERVAL = 2 * 60 * 1000;

    const askQuestion = () => {
      if (showQuestionPopup) return;
      if (!Array.isArray(availableQuestions) || availableQuestions.length === 0) return;

      const MAX_QUESTIONS = 10;
      const already = askedQuestionsRef.current || [];
      if (already.length >= MAX_QUESTIONS) return;

      const remaining = availableQuestions.filter((q) => !already.includes(q.id));
      if (!remaining.length) return;

      const random = remaining[Math.floor(Math.random() * remaining.length)];
      if (!random?.prompt) return;

      setCurrentQuestion(random);
      setShowQuestionPopup(true);
      setAskedQuestionIds((prev) => {
        const next = [...prev, random.id];
        askedQuestionsRef.current = next;
        return next;
      });
    };

    firstTimeoutRef.current = setTimeout(() => {
      askQuestion();
      intervalRef.current = setInterval(askQuestion, QUESTION_INTERVAL);
    }, QUESTION_FIRST_DELAY);

    return () => {
      if (firstTimeoutRef.current) clearTimeout(firstTimeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activeMeeting?.meeting_id, availableQuestions]);

  const handleQuestionSubmit = async () => {
    if (!activeMeeting?.meeting_id || !currentQuestion || !user?.id) return;

    try {
      const payload = {
        asked_by_user_id: user.id,
        question_id: currentQuestion.id,
        question_category: currentQuestion.resolvedCategory || "unknown",
        asked_at: new Date().toISOString(),
      };
      await logMeetingQuestion(activeMeeting.meeting_id, payload);
      toast.success("Question logged successfully");
    } catch (err) {
      console.error("Error saving question:", err);
      toast.error("Failed to save question");
    } finally {
      setShowQuestionPopup(false);
    }
  };

  const filteredProfiles = profiles.filter((profile) => {
    if (genderFilter !== "all" && profile.gender !== genderFilter) return false;
    if (nameFilter.trim() !== "") {
      const fullName = `${profile.first_name} ${profile.last_name}`.toLowerCase();
      const searchTerm = nameFilter.toLowerCase();
      return fullName.includes(searchTerm) || profile.first_name.toLowerCase().includes(searchTerm) || profile.last_name.toLowerCase().includes(searchTerm);
    }
    return true;
  });
  const shouldShowVideoCall = activeMeeting && (activeMeeting.meeting_context === "video_call" || activeMeeting.meeting_context === "audio_call");
  const isLoading = selectedWindow === "latest" ? resultsLoading : trajectoryLoading;
  if (!user) return <div className="meeting-container">Loading user data...</div>;

  return (
    <div className="meeting-container">
      <div className="meeting-layout-redesign">
        {/* Left Panel */}
        <section className="left-panel-redesign">
          <div className="user-list-section">
            <div className="section-header">
              <h2>Friends List</h2>
              <button onClick={loadProfiles} className="refresh-btn" disabled={loading}>
                {loading ? "Loading..." : "↻ Refresh"}
              </button>
            </div>

            <FiltersPanel
              genderFilter={genderFilter}
              onGenderFilter={setGenderFilter}
              nameFilter={nameFilter}
              onNameFilter={setNameFilter}
            />

            {activeMeeting && (
              <div className="meeting-active-warning">
                <span className="warning-icon">⚠️</span>
                <span>
                  Meeting in progress with {selectedUser?.first_name} {selectedUser?.last_name}
                  {activeMeeting?.meeting_context ? ` (${toNormalText(activeMeeting.meeting_context)})` : ""}
                </span>
              </div>
            )}

            {loading ? (
              <div className="loading">Loading users...</div>
            ) : filteredProfiles.length > 0 ? (
              <UserList
                profiles={filteredProfiles}
                selectedUserId={selectedUser?.user_id}
                onUserSelect={handleUserSelect}
                activeMeeting={activeMeeting}
                isSelectionDisabled={!!activeMeeting}
              />
            ) : (
              <div className="no-users">No users found. Try changing filters.</div>
            )}
          </div>
        </section>

        {/* Main Content */}
        <section className="main-content-redesign">
          <div className="profile-comparison-container">
            <ProfileComparison
              currentUser={user}
              selectedUser={selectedUser}
              activeMeeting={activeMeeting}
              meetingStartTime={meetingStartTime}
              profiles={filteredProfiles}
              selectedUserId={selectedUser?.user_id}
              onUserSelect={handleUserSelect}
              onDeselectUser={handleDeselectUser}
              isSelectionDisabled={!!activeMeeting}
            />
          </div>

          <div className="meeting-controls-container">
            <MeetingControls
              selectedUser={selectedUser}
              currentUser={user}
              activeMeeting={activeMeeting}
              onStartMeeting={handleStartMeeting}
              onEndMeeting={handleEndMeeting}
              meetingStartTime={meetingStartTime}
              onDeselectUser={handleDeselectUser}
            />
          </div>
          
          {/* Inline video call removed – now handled by modal */}
          
          <CompatibilityResults
            selectedUser={selectedUser}
            liveResult={liveResult}
            allMeetingsData={allMeetingsData}
            relationshipTrajectory={relationshipTrajectory}
            selectedWindow={selectedWindow}
            setSelectedWindow={setSelectedWindow}
            isLoading={isLoading}
            user={user}
            getWindowLabel={getWindowLabel}
            trendMeaning={trendMeaning}
            sharedStateMeaning={sharedStateMeaning}
            convertSnakeCaseInText={convertSnakeCaseInText}
            toNormalText={toNormalText}
            setShowToolTipPopup={setShowToolTipPopup}
            trajectoryLoading={trajectoryLoading}
          />
        </section>
      </div>

      {showStartMeetingPopup && (
        <MeetingHeader
          selectedUser={selectedUser}
          isVideoMeeting={isVideoMeeting}
          setIsVideoMeeting={setIsVideoMeeting}
          selectedMeetingMode={selectedMeetingMode}
          setSelectedMeetingMode={setSelectedMeetingMode}
          startingMeeting={startingMeeting}
          handleConfirmStartMeeting={handleConfirmStartMeeting}
          closeStartMeetingPopup={closeStartMeetingPopup}
        />
      )}

      {showQuestionPopup && currentQuestion && (
        <QuestionPopup
          question={currentQuestion}
          onClose={() => setShowQuestionPopup(false)}
          onSubmit={handleQuestionSubmit}
        />
      )}

      {showToolTipPopup && (
        <ToolTips stateDetails={stateDetails} onClose={() => setShowToolTipPopup(false)} />
      )}

      {/* Video Call Modal with Minimize/Maximize */}
      {showVideoCallModal && activeMeeting && shouldShowVideoCall && (
        <div className={`video-call-modal-overlay ${isVideoCallMinimized ? 'minimized-overlay' : ''}`}>
          <div className={`video-call-modal-container ${isVideoCallMinimized ? 'minimized-container' : ''}`}>
            <VideoCall
              ref={videoCallRef}
              roomId={String(activeMeeting.meeting_id)}
              userId={user?.id}
              token={token}
              meetingId={activeMeeting.meeting_id}
              user={user}
              selectedUser={selectedUser}
              user1Id={activeMeeting.user_1}
              user2Id={activeMeeting.user_2}
              onMeetingEnd={() => {
                handleEndMeeting()
                setTimeout(() => {
                  fetchAllResults();
                  if (selectedWindow !== "latest") fetchRelationshipTrajectory(selectedWindow);
                }, 1000);
              }}
              isMinimized={isVideoCallMinimized}
              onToggleMinimize={toggleVideoCallMinimize}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Meeting;